import { observer } from "mobx-react-lite";
import { useState } from "react";

import {
  Card,
  confirm,
  FontWeights,
  Image,
  Spinner,
  Stack,
  Text,
  Toggle
} from "@bps/fluent-ui";
import {
  OrgUnitCompanyDataType,
  OrgUnitEnabledXeroDto
} from "@libs/gateways/practice/PracticeGateway.dtos.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { DataFetcher } from "@ui-components/data-fetcher/DataFetcher.tsx";

import xeroLogoBlue from "../assets/xeroLogoBlue.svg";
import { useIntegrationScreenContext } from "../context/IntegrationScreenContext.ts";

const XeroLogo = () => {
  return <Image width="154.4" height="27" src={xeroLogoBlue} alt="Xero logo" />;
};

export const XeroCard: React.FC = observer(() => {
  const { practice, core, notification } = useStores();
  const [isXeroConfigLoading, setisXeroConfigLoading] = useState(false);
  const { setShowXeroConfigurationCard, showXeroConfigurationCard } =
    useIntegrationScreenContext();

  const getOrgUnit = async () => {
    const orgUnit = await practice.getOrgUnit(core.location?.id);
    const isEnabled = orgUnit?.orgUnitEnabledXero?.enabled;
    setShowXeroConfigurationCard(!!isEnabled);
  };

  const onChangeHandle = async (checked: boolean | undefined) => {
    if (checked) {
      try {
        setisXeroConfigLoading(true);
        await practice.updateOrgUnit({
          id: core.location?.id, // TODO - this probably needs improving
          orgUnitCompanyData: [
            {
              orgUnitCompanyDataTypeCode: OrgUnitCompanyDataType.Xero,
              enabled: checked
            } as OrgUnitEnabledXeroDto
          ]
        });
        setShowXeroConfigurationCard(!!checked);
      } catch (error) {
        notification.error(error.message);
      } finally {
        setisXeroConfigLoading(false);
      }
    } else {
      const isConfirmed = await confirm({
        children: (
          <Text block variant="medium" styles={{ root: { paddingBottom: 16 } }}>
            Do you want to disable the Xero integration?
          </Text>
        ),
        minWidth: 560,
        dialogContentProps: {
          title: "Disabling integration"
        },
        confirmButtonProps: {
          text: "Disable"
        },
        cancelButtonProps: {
          text: "Cancel"
        }
      });
      if (isConfirmed) {
        try {
          await practice.updateOrgUnit({
            id: core.location?.id,
            orgUnitCompanyData: [
              {
                orgUnitCompanyDataTypeCode: OrgUnitCompanyDataType.Xero,
                enabled: checked
              } as OrgUnitEnabledXeroDto
            ]
          });
          setShowXeroConfigurationCard(!!checked);
        } catch (error) {
          notification.error(error.message);
        }
      }
    }
  };

  return (
    <Card
      heading={
        <Stack
          horizontal
          horizontalAlign="space-between"
          styles={{
            root: { width: "100%" }
          }}
        >
          <XeroLogo />
        </Stack>
      }
      styles={{
        heading: { fontSize: 20, fontWeight: FontWeights.semibold },
        subHeading: {
          fontSize: 12
        },
        subComponentStyles: {
          tile: {
            content: { padding: 24 }
          }
        }
      }}
    >
      <Stack
        horizontal
        horizontalAlign="space-between"
        tokens={{ childrenGap: 16 }}
      >
        <Stack grow verticalAlign="start" tokens={{ childrenGap: 4 }}>
          <Text variant="medium">
            Cloud-based accounting software for your practice
          </Text>
        </Stack>
        {!isXeroConfigLoading ? (
          <DataFetcher
            fetch={getOrgUnit}
            renderError={error => notification.error(error.message)}
            refetchId={practice.ui.recentlyUpdatedOrgUnitEtag}
            fallback={<Spinner />}
          >
            {() => (
              <Toggle
                onText="Enabled"
                offText="Disabled"
                automationAttribute=""
                checked={showXeroConfigurationCard}
                onChange={(_evt, checked) => onChangeHandle(checked)}
              />
            )}
          </DataFetcher>
        ) : (
          <Spinner />
        )}
      </Stack>
    </Card>
  );
});
